/**
 * Assignment Card — Shows active assignment or recent evaluation in sidebar/header.
 * Per spec Section 4.5: active assignment always visible.
 */

import { Clock, Target, CheckCircle, Award } from 'lucide-react';

const SCORE_CONFIG = {
  1: { color: 'text-red-400', bg: 'bg-red-900/50', label: 'Not attempted' },
  2: { color: 'text-amber-400', bg: 'bg-amber-900/50', label: 'Attempted' },
  3: { color: 'text-yellow-400', bg: 'bg-yellow-900/50', label: 'Met criteria' },
  4: { color: 'text-green-400', bg: 'bg-green-900/50', label: 'Strong delivery' },
  5: { color: 'text-emerald-400', bg: 'bg-emerald-900/50', label: 'Exceeded expectations' },
};

function getTimeRemaining(deadline) {
  const now = new Date();
  const dl = new Date(deadline);
  const diff = dl - now;

  if (diff < 0) return { text: 'Overdue', urgent: true };

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) return { text: `${days}d ${hours % 24}h remaining`, urgent: days < 1 };
  if (hours > 0) return { text: `${hours}h remaining`, urgent: hours < 6 };
  return { text: 'Due soon', urgent: true };
}

function EvaluatedCard({ evaluation }) {
  const score = evaluation.evaluation_score;
  const config = SCORE_CONFIG[score] || SCORE_CONFIG[3];

  return (
    <div className="bg-green-950/30 border border-green-800/50 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Award size={16} className="text-green-400" />
        <span className="text-sm font-medium text-green-400">Assignment Evaluated</span>
        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
          {score}/5 — {config.label}
        </span>
      </div>

      <p className="text-sm text-slate-200 mb-2">{evaluation.goal_text}</p>

      {evaluation.evaluation_text && (
        <p className="text-xs text-slate-400 mt-1">{evaluation.evaluation_text}</p>
      )}
    </div>
  );
}

export default function AssignmentCard({ assignment, evaluatedAssignment }) {
  if (evaluatedAssignment && evaluatedAssignment.status === 'evaluated') {
    return <EvaluatedCard evaluation={evaluatedAssignment} />;
  }

  if (!assignment) return null;

  const deadline = getTimeRemaining(assignment.deadline);

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Target size={16} className="text-amber-400" />
        <span className="text-sm font-medium text-amber-400">Active Assignment</span>
        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
          deadline.urgent ? 'bg-red-900/50 text-red-400' : 'bg-slate-700 text-slate-400'
        }`}>
          <Clock size={12} className="inline mr-1" />
          {deadline.text}
        </span>
      </div>

      <p className="text-sm text-slate-200 mb-2">{assignment.goal_text}</p>

      {assignment.criteria && assignment.criteria.length > 0 && (
        <div className="space-y-1 mb-2">
          {assignment.criteria.map((c, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-slate-400">
              <CheckCircle size={12} className="mt-0.5 shrink-0" />
              <span>{c}</span>
            </div>
          ))}
        </div>
      )}

      {assignment.who_it_helps && (
        <p className="text-xs text-slate-500 mt-2">
          Who it helps: <span className="text-slate-400">{assignment.who_it_helps}</span>
        </p>
      )}
    </div>
  );
}
